import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getEntityId } from '../../utils/navigation-utils';
import ErpLoader from '../loaders/erp-loader';

interface EntityViewWrapperProps<T> {
  /**
   * Function to fetch entity data by ID
   */
  fetchEntity: (id: string) => Promise<T>;
  
  /**
   * Route to navigate to if entity is not found
   */
  fallbackRoute: string;
  
  /**
   * Component to render with entity data
   */
  children: (entity: T) => React.ReactNode;
  
  /**
   * Optional ID field name in location.state (default: 'id')
   */
  idField?: string;
}

/**
 * A wrapper component that handles fetching entity data using either URL parameters or location state
 * This provides backward compatibility during migration from state-based to URL parameter-based routing
 */
function EntityViewWrapper<T>({
  fetchEntity,
  fallbackRoute,
  children,
  idField = 'id'
}: EntityViewWrapperProps<T>) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [entity, setEntity] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadEntity = async () => {
      setLoading(true);
      
      // Get entity ID from URL params or location state
      const entityId = getEntityId(params, location, idField);
      
      if (!entityId) {
        // If no ID is found, navigate to fallback route
        navigate(fallbackRoute);
        return;
      }
      
      try {
        // Fetch entity data
        const data = await fetchEntity(entityId);
        setEntity(data);
      } catch (error) {
        console.error('Error fetching entity:', error);
        // If entity is not found, navigate to fallback route
        navigate(fallbackRoute);
      } finally {
        setLoading(false);
      }
    };
    
    loadEntity();
  }, [fetchEntity, params, location, navigate, fallbackRoute, idField]);
  
  if (loading) {
    return <ErpLoader />;
  }
  
  if (!entity) {
    return null;
  }
  
  return <>{children(entity)}</>;
}

export default EntityViewWrapper; 